/**
 * reports.service.ts — acceso a /informes vía el http-client compartido.
 * Único lugar donde el feature reports habla con el API.
 */
import { httpDelete, httpGet, httpPatch, httpPost } from "@/shared/lib/http-client.js";
import type {
  Cliente,
  EstadoInforme,
  Informe,
  InformeFiltros,
  NuevoInformeInput,
} from "../types.js";

/** GET /informes — el backend filtra por rol (el técnico ve solo los suyos). */
export async function listInformes(filtros: InformeFiltros = {}): Promise<Informe[]> {
  const params = new URLSearchParams();
  if (filtros.tipo) params.set("tipo", filtros.tipo);
  if (filtros.estado) params.set("estado", filtros.estado);
  if (filtros.since) params.set("since", filtros.since);
  const qs = params.toString();
  return httpGet<Informe[]>(`/informes${qs ? `?${qs}` : ""}`);
}

/** PATCH /informes/:id/estado — transición de estado (el server valida la transición). */
export async function changeEstadoInforme(id: string, estado: EstadoInforme): Promise<Informe> {
  return httpPatch<Informe>(`/informes/${id}/estado`, { estado });
}

/**
 * POST /informes/:id/pdf — genera el PDF (pdfmake) y lo sube al storage.
 * Si el storage falla en runtime (cupo agotado, red), degrada: pdfUrl null +
 * degraded true. El detalle igual muestra el PDF renderizado en el device.
 */
export async function generarPdfInforme(
  id: string,
): Promise<{ pdfUrl: string | null; degraded: boolean }> {
  return httpPost<{ pdfUrl: string | null; degraded: boolean }>(`/informes/${id}/pdf`);
}

/**
 * POST /informes — crea un informe. El número se autogenera en el backend.
 * Enviar EITHER `clienteId` (existente) OR `cliente` (nombre → find-or-create).
 */
export async function createInforme(input: NuevoInformeInput): Promise<Informe> {
  return httpPost<Informe>("/informes", input);
}

/** GET /informes/:id — un informe por id (para el editor). */
export async function getInforme(id: string): Promise<Informe> {
  return httpGet<Informe>(`/informes/${id}`);
}

/**
 * PATCH /informes/:id — guarda el payload COMPLETO del informe.
 * Enviamos siempre el objeto entero (no dependemos de merge en el backend).
 */
export async function updateInformePayload(
  id: string,
  payload: Record<string, unknown>,
): Promise<Informe> {
  return httpPatch<Informe>(`/informes/${id}`, { payload });
}

/** DELETE /informes/:id — elimina un informe (solo borradores propios; 204). */
export async function deleteInforme(id: string): Promise<void> {
  return httpDelete(`/informes/${id}`);
}

/**
 * filterClientesLocally — filter a cached clientes array by query string.
 * Case-insensitive partial match on nombre or sigla.
 * Pure function (exported for testing).
 */
export function filterClientesLocally(clientes: Cliente[], q: string): Cliente[] {
  if (!q.trim()) return clientes;
  const lower = q.toLowerCase();
  return clientes.filter(
    (c) => c.nombre.toLowerCase().includes(lower) || c.sigla.toLowerCase().includes(lower),
  );
}

/** GET /clientes?q= — autocomplete del combobox find-or-create.
 *  S4-T5: when offline, falls back to the cached clientes catalog with local filtering.
 */
export async function searchClientes(q: string): Promise<Cliente[]> {
  if (!navigator.onLine) {
    const { getCachedCatalog } = await import("@/features/offline-sync/services/catalog-cache.js");
    const cached = await getCachedCatalog<Cliente[]>("clientes", () => Promise.reject(new Error("offline")));
    return filterClientesLocally(cached, q);
  }
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  return httpGet<Cliente[]>(`/clientes${qs}`);
}

/**
 * prewarmCatalogs — while online, fetch the FULL clientes list + marca and cache
 * them to IDB so the técnico has the same data offline (combobox, branding).
 *
 * searchClientes only ever fetches a filtered `?q=` slice and never caches the
 * full list, and the admin's listClientes is never called by a técnico — so
 * without this prewarm the offline clientes cache stays empty. Best-effort:
 * failures are swallowed (offline / transient) so callers never block.
 */
export async function prewarmCatalogs(): Promise<void> {
  if (!navigator.onLine) return;
  const { cacheCatalog } = await import("@/features/offline-sync/services/catalog-cache.js");
  await Promise.allSettled([
    cacheCatalog("clientes", () => httpGet<Cliente[]>("/clientes")),
    cacheCatalog("marca", () => httpGet<unknown>("/marca")),
  ]);
}
